import * as React from 'react';
import DocumentTitle from 'react-document-title';
import { StylesProps } from '../../theme/jss-types';
import {
    ProfileOutlined,
    LogoutOutlined,
    LoginOutlined,
    UserOutlined,
    EnvironmentOutlined,
    GlobalOutlined,
} from '@ant-design/icons';
import Loader from './Loader';
import {
    Button, Divider, Dropdown, Input, Menu, message, Select,
} from 'antd';
import PincodeService from '../pincode-service/service-availability';
import TrackConsignment from '../track-consignment/track-consignment';
import authHelper from '../../auth/auth-helper';
import { HocOptions } from './generic-hoc.types';
import GenericHoc from './generic-hoc';
import { navbarStyles } from './navbar.styles';
import { RouteComponentProps } from 'react-router';
import { getSettingsAccountRoute, getRateCalculatorRoute } from 'routing/routing-helper';
import { ReduxStore } from 'reducers/redux.types';
import { Master } from 'types/master-data-types';
import Helper from 'library/Helper';
import { useTranslation } from 'react-i18next';
import RateIcon from 'assets/rate-svg';
import { isSuperUserEmployee } from 'auth/local-storage';
import ChangeCustomer from './change-customer';
import { TrackTypeEnum } from 'types/view.types';
import QuickActionIcon from 'assets/quick-action';
import { languageCodeMapping, RouteDetails, Routes } from 'library/constants';
import EmptyIcon from 'assets/empty-space-icon';
import classNames from 'classnames';
import TargetIcon from 'assets/target-icon';
import WalletIcon from 'assets/wallet';
import { storeCNCDataState } from 'actions/miscellaneous-actions';
import { bindActionCreators } from 'redux';
import { fetchCustomerWalletData } from 'network/wallet.api';
import useIsMobile from 'hooks/useIsMobile';
import NavbarMobile from './NavbarMobile';

interface NavbarProps extends StylesProps<ReturnType<typeof navbarStyles>>, RouteComponentProps {
    pageTitle: string;
    master: Master;
    i18n: any;
    routesInfo: Record<string, RouteDetails>;
    cncData: Record<string, any>;
    setCNCData: (data: any) => void;
}

const Navbar = (props: NavbarProps) => {
    const {
        pageTitle,
        classes,
        history,
        master,
        i18n,
        routesInfo,
        cncData,
        setCNCData,
    } = props;

    const isMobile = useIsMobile();
    const { t } = useTranslation();
    const [pincodeDrawer, setPincodeDrawer] = React.useState(false);
    const [showChangeCustomer, setChangeCustomerModal] = React.useState(false);
    const [trackVisible, setTrackVisible] = React.useState<string>();
    const [trackQuery, setTrackQuery] = React.useState<string>();
    const [trackType, setTrackType] = React.useState<string>('consignment_number');
    const currentRoute = Helper.getCurrentRoute();
    const showWalletBalance = master?.Customer?.cnc_flag || false;
    const [loading, setLoading] = React.useState(false);

    const oldBaseURL = master?.config?.customer_portal_config?.old_cp_base_url;

    const show_rate_calculator = master?.config?.customer_portal_config?.show_rate_calculator
        && master?.config?.customer_portal_config?.enable_serviceability;
    const show_old_portal_link = master?.config?.customer_portal_config?.show_old_portal_link;
    const navbar_background_color = master?.config?.customer_portal_config?.navbar_background_color;

    const customerProperties = master?.properties;
    const enable_customer_config = master?.parts_to_show?.enable_customer_config || false;
    const search_carrier_awb_number = master?.properties?.search_carrier_awb_number;
    const precisionValue = master?.config?.precision_value || 3;
    let showPincodeServiceability = true;
    if (customerProperties && enable_customer_config) {
        showPincodeServiceability = customerProperties?.pincode_serviceability;
    }

    const { config } = master;
    const { customer_portal_ops_config, available_language_list } = config;
    let languageList: any = [];
    const isLanguageChangeAllowed = customer_portal_ops_config?.language_config?.config_value?.is_language_change_allowed
        || false;
    if (customer_portal_ops_config?.language_config?.config_value) {
        const allowed_language = customer_portal_ops_config.language_config
            ?.config_value?.allowed_language_list_customer_portal || [];
        if (Array.isArray(available_language_list) && Array.isArray(allowed_language)
                && available_language_list.length > 0 && allowed_language.length > 0) {
            languageList = available_language_list
                .filter((item: any) => allowed_language.includes(item.language_name));
        }
    }

    const {
        logoUrl,
    } = authHelper.getUsernameAndLogo();

    const isSuperUser = isSuperUserEmployee();


    const loadCNCData = async () => {
        if (!showWalletBalance) {
            return;
        }
        const response = await fetchCustomerWalletData();
        if (!response?.isSuccess) {
            message.error('Unable to fetch wallet balance');
        } else {
            setCNCData(response?.data || {});
        }
    };

    React.useEffect(() => {
        loadCNCData();
    }, [showWalletBalance]);


    const handleLanguageChange = (val: any) => {
        i18n.changeLanguage(val, (err: any) => {
            if (err) {
                message.error(err);
                return;
            }
            message.success('Language changed successfully');
            window.location.reload();
        });
    };

    const handleLogout = async () => {
        setLoading(true);
        await authHelper.logoutUser();
        setLoading(false);
    };

    const menu = (
        <Menu>
            <Menu.Item
                key="profile"
                onClick={() => history.push(getSettingsAccountRoute())}
                icon={<ProfileOutlined />}
            >
                {t('profile')}
            </Menu.Item>
            {show_old_portal_link && (
                <Menu.Item
                    key="old_cp"
                    onClick={() => Helper.redirectToOldPortal(oldBaseURL)}
                    icon={<LoginOutlined />}
                >
                    {t('legacy_customer_portal')}
                </Menu.Item>
            )}
            {isSuperUser && (
                <Menu.Item
                    key="change_customer"
                    onClick={() => setChangeCustomerModal(true)}
                    icon={<UserOutlined />}
                >
                    {t('change_customer')}
                </Menu.Item>
            )}
            {
                isLanguageChangeAllowed
                && (
                    <Menu.Item
                        key="language"
                        icon={<GlobalOutlined />}
                        onClick={(e) => e.domEvent.stopPropagation()}
                    >
                        <Select
                            onChange={handleLanguageChange}
                            value={`${t('Language')}: ${languageCodeMapping[i18n.language]?.prettyName}`}
                            defaultValue={languageCodeMapping[i18n.language]?.prettyName}
                            style={{ width: '150px' }}
                            className={classes.changeLanguageDropdown}
                            options={languageList?.map((item: any) => {
                                return {
                                    value: item.key,
                                    label: item.language_name,
                                };
                            }) || []}
                            onClick={(e) => e.stopPropagation()}
                            suffixIcon={<EmptyIcon />}
                        />
                    </Menu.Item>
                )
            }
            <Menu.Item
                key="logout"
                icon={<LogoutOutlined />}
                onClick={handleLogout}
                disabled={loading}
            >
                {t('logout')}
            </Menu.Item>
        </Menu>
    );

    const renderProfile = () => {
        return (
            <div>
                <div className={classes.profile}>
                    <UserOutlined />
                </div>
            </div>
        );
    };

    const renderProfileButton = () => {
        return (
            <Dropdown
                overlay={menu}
                trigger={['click']}
            >
                <Button
                    type="text"
                    className={classNames(classes.username, 'logout-button')}
                >
                    {renderProfile()}
                </Button>
            </Dropdown>
        );
    };

    const quickMenu = (
        <Menu>
            { showPincodeServiceability && !master?.config?.customer_portal_consignment_config?.is_international_client
            && (
                <Menu.Item
                    onClick={() => {
                        if (currentRoute !== Routes.PINCODE_SERVICEABILITY) {
                            setPincodeDrawer(true);
                        }
                    }}
                    icon={<EnvironmentOutlined />}
                    className="pincode-serviceability"
                    key="pincode_serviceability"
                >
                    {t('check_pincode_serviceability')}
                </Menu.Item>
            )}
            {show_rate_calculator && (
                <Menu.Item
                    onClick={() => {
                        if (currentRoute !== Routes.RATE_CALCULATOR) {
                            history.push(getRateCalculatorRoute());
                        }
                    }}
                    icon={<RateIcon />}
                    className="calculate-rates"
                    key="rate_calculator"
                >
                    {t('Rate Calculator')}
                </Menu.Item>
            )}
        </Menu>
    );

    const renderQuickAction = () => {
        if ((!showPincodeServiceability
            || master?.config?.customer_portal_consignment_config?.is_international_client)
            && !show_rate_calculator) {
            return null;
        }
        return (
            <Dropdown
                overlay={quickMenu}
                trigger={['click']}
                className={classes.quickAction}
            >
                <Button
                    type="text"
                    icon={<QuickActionIcon />}
                    className="quick-action"
                >
                    {t('quick_actions')}
                </Button>
            </Dropdown>
        );
    };

    const renderSearchSelect = () => {
        let ioptions = [
            {
                value: TrackTypeEnum.CONSIGNMENT,
                label: t(TrackTypeEnum.CONSIGNMENT),
            },
            {
                value: TrackTypeEnum.CUSTOMER_REFERENCE_NO,
                label: t(TrackTypeEnum.CUSTOMER_REFERENCE_NO),
            },
        ];
        if (search_carrier_awb_number) {
            ioptions = [
                ...ioptions,
                {
                    value: TrackTypeEnum.CARRIER_AWB_NUMBER,
                    label: t(TrackTypeEnum.CARRIER_AWB_NUMBER),
                },
            ];
        }
        return (
            <Select
                className={classes.searchSelect}
                defaultValue={trackType}
                onChange={(value: string) => setTrackType(value)}
                dropdownMatchSelectWidth={false}
                options={ioptions}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
            />
        );
    };

    const renderSearch = () => {
        return (
            <Input
                onPressEnter={() => setTrackVisible(trackQuery)}
                value={trackVisible as string}
                onChange={(e: any) => setTrackQuery(e.target.value)}
                placeholder={
                    (trackType && trackType === 'customer_reference_number')
                        ? t('track_customer_reference_number')
                        : t('track_consignment')
                }
                prefix={<TargetIcon onClick={() => setTrackVisible(trackQuery)} />}
                className={classes.search}
            />
        );
    };

    const renderInput = () => {
        return (
            <div className={classNames(classes.trackConsignment, 'consignment-tracking')}>
                {renderSearchSelect()}
                {renderSearch()}
            </div>
        );
    };

    const handleCloseForm = () => {
        setPincodeDrawer(false);
        setTrackVisible(undefined);
    };

    const renderTrack = () => {
        if (!trackVisible) {
            return null;
        }
        return (
            <TrackConsignment
                referenceNumber={trackVisible as string}
                onClose={handleCloseForm}
                trackType={trackType as string}
            />
        );
    };

    const renderPincodeDrawer = () => {
        if (!pincodeDrawer) {
            return null;
        }
        return (
            <PincodeService
                onClose={handleCloseForm}
                shouldRenderInDrawer
            />
        );
    };

    const renderChangeCustomer = () => {
        if (!showChangeCustomer) {
            return null;
        }
        return (
            <ChangeCustomer
                onClose={() => setChangeCustomerModal(false)}
            />
        );
    };

    const renderWalletBalance = () => {
        if (!showWalletBalance) {
            return null;
        }
        const availableBalance = Number(cncData?.availableBalance || 0);
        return (
            <Button
                type="text"
                className={classNames(classes.walletBalance, 'wallet-balance')}
                icon={<WalletIcon />}
                onClick={() => history.push(`/${Routes.WALLET_LEDGER}`)}
            >
                {Intl.NumberFormat('en-IN', {
                    maximumFractionDigits: precisionValue,
                }).format(availableBalance)}
                &nbsp;INR
            </Button>
        );
    };

    if (isMobile) {
        return (
            <NavbarMobile
                loading={loading}
                trackType={trackType}
                setTrackType={setTrackType}
                trackVisible={trackVisible}
                setTrackVisible={setTrackVisible}
                trackQuery={trackQuery}
                setTrackQuery={setTrackQuery}
                pageTitle={pageTitle}
            />
        );
    }

    return (
        <div
            className={classes.mainDiv}
            style={navbar_background_color ? { backgroundColor: navbar_background_color } : undefined}
        >
            {loading && <Loader zIndex={10} />}
            <DocumentTitle title={pageTitle} />
            <div className={classes.leftBox}>
                <div className={classes.logoBox}>
                    <img
                        alt="logo-url"
                        src={logoUrl as string}
                        style={{
                            height: '100%',
                        }}
                    />
                </div>
                &nbsp;
                <Divider type="vertical" className={classes.titleBar} />
                <div className={classes.pageTitle}>
                    {t(routesInfo[currentRoute]?.title)}
                </div>
            </div>
            <div className={classes.rightBox}>
                {renderInput()}
                {renderQuickAction()}
                {renderWalletBalance()}
                {renderProfileButton()}
            </div>
            {renderPincodeDrawer()}
            {renderTrack()}
            {renderChangeCustomer()}
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { master, miscellaneousState } = state;
    return {
        master,
        routesInfo: miscellaneousState?.ROUTES_INFO || {},
        cncData: miscellaneousState?.CNC_INFO || {},
    };
};

const mapDispatchToProps = (dispatch: any) => (bindActionCreators({
    setCNCData: storeCNCDataState,
}, dispatch));

const hocConfig: HocOptions = {
    connectRedux: {
        useRedux: true,
        mapStateToProps,
        mapDispatchToProps,
    },
    connectJss: {
        useJss: true,
        styleSheet: navbarStyles,
    },
    connectRouter: true,
};

export default GenericHoc(hocConfig)(Navbar);
